import customNanoId from 'lib/nanoid/nanoid';
import {
  LANGUAGE_CODE_EN_US,
  LANGUAGE_CODE_ES_US,
  LanguageCodes,
} from 'global/constants';
import {
  DictionaryDefinition,
  FormDefinition,
  FormExample,
  FormWordsTranslation,
} from './vocabulary-setting.constants';
import type { TranslateToQuery } from '../../../../dictionary/dictionary.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export function validateHasValue(value?: string) {
  return !value || value.trim().length === 0
    ? 'Please enter a value'
    : undefined;
}

export function validateHasImageData(example?: FormExample) {
  if (!example?.image?.key) {
    return 'Don’t forget to upload an image for this example sentence.';
  }
  return example?.image?.key && !example.image.alt
    ? 'Hold on! Click the image and add some alt text first.'
    : undefined;
}

export function validateForm(
  definitions: FormDefinition[],
  examples: FormExample[],
  wordsTranslation?: FormWordsTranslation[]
) {
  let hasError = false;

  for (let i = 0; i < definitions.length; i++) {
    const item = definitions[i];
    item.error = validateHasValue(item.description);
    if (item.error) {
      hasError = true;
    }
  }

  for (let i = 0; i < examples.length; i++) {
    const item = examples[i];
    item.error = validateHasImageData(item) || validateHasValue(item.example);
    if (item.error) {
      hasError = true;
    }
  }
  if (wordsTranslation) {
    // The first word is the English word, and cannot be edited in the UI so we skip it
    for (let i = 1; i < wordsTranslation.length; i++) {
      wordsTranslation[i].error = validateHasValue(wordsTranslation[i].word);
      if (wordsTranslation[i].error) {
        hasError = true;
      }
    }
  }

  return {
    definitions,
    examples,
    wordsTranslation,
    hasError,
  };
}

export function getFieldErrors(
  definitions: FormDefinition[],
  examples: FormExample[],
  wordsTranslation: FormWordsTranslation[]
) {
  const definitionErrors = definitions.filter(e => e.error);

  const exampleErrors = examples.filter(e => e.error);

  const wordErrors = wordsTranslation.filter(e => e.error);
  return [
    ...definitionErrors.map(def => ({
      id: def.id,
      language: def.language,
    })),
    ...exampleErrors.map(ex => ({
      id: ex.exampleId,
      language: ex.language,
    })),
    ...wordErrors.map(w => ({
      id: wordsTranslation[0].word, // the form uses the English word as the id
      language: w.language,
    })),
  ];
}

export function getTabOptions(definitions: FormDefinition[]) {
  const tabs = [
    {
      label: 'English',
      value: LANGUAGE_CODE_EN_US,
    },
  ];

  if (definitions.some(d => d.language === LANGUAGE_CODE_ES_US)) {
    tabs.push({
      label: 'Spanish',
      value: LANGUAGE_CODE_ES_US,
    });
  }
  return tabs;
}

export function collectSentences(
  definitions: FormDefinition[],
  examples: FormExample[],
  wordsTranslations: FormWordsTranslation[]
): { translationId: string; sentence: string }[] {
  const sentences = definitions.map(def => {
    return {
      translationId: def.id,
      sentence: def.description || '',
    };
  });
  for (let i = 0; i < examples.length; i++) {
    const example = examples[i];
    sentences.push({
      translationId: example.exampleId,
      sentence: example.example,
    });
    if (example.image?.key && example.image.alt) {
      sentences.push({
        translationId: example.image.key,
        sentence: example.image.alt,
      });
    }
  }
  for (let i = 0; i < wordsTranslations.length; i++) {
    const wordTranslation = wordsTranslations[i];
    sentences.push({
      translationId: wordTranslation.word,
      sentence: wordTranslation.word,
    });
  }
  return sentences;
}

export function assignTranslations({
  definitions,
  examples,
  translations,
  language,
  wordsTranslation,
}: {
  definitions: FormDefinition[];
  examples: FormExample[];
  translations: Pluck<TranslateToQuery, 'translateTo.translations'>[];
  language: string;
  wordsTranslation: FormWordsTranslation[];
}) {
  for (let i = 0; i < translations.length; i++) {
    const translation = translations[i];
    const definition = definitions.find(
      d => d.id === translation.translationId
    );
    if (definition) {
      definitions.push({
        id: customNanoId(),
        description: translation.translation,
        year: definition.year,
        language: LANGUAGE_CODE_ES_US,
      });
      continue;
    }

    const example = examples.find(
      ex => ex.exampleId === translation.translationId
    );
    if (example) {
      examples.push({
        exampleId: example.exampleId,
        example: translation.translation,
        image: { ...example.image },
        language: LANGUAGE_CODE_ES_US,
      });
      continue;
    }

    const exampleImage = examples.find(
      ex =>
        ex.image?.key === translation.translationId && ex.language === language
    );
    if (exampleImage?.image?.key) {
      exampleImage.image.alt = translation.translation;
    }

    const wordTranslation = wordsTranslation.find(
      w => w.word === translation.translationId
    );

    if (wordTranslation) {
      wordsTranslation.push({
        word: translation.translation.trim(),
        language: LANGUAGE_CODE_ES_US,
      });
    }
  }
  return {
    definitions,
    examples,
    wordsTranslation,
  };
}

export function hasTranslations({
  definitions,
  language,
}: {
  definitions: FormDefinition[];
  language: LanguageCodes;
}) {
  return definitions.some(d => d.language === language);
}

export function hasSavedTranslations({
  allDefinitions,
  language,
  currentDefinitionId,
}: {
  allDefinitions: DictionaryDefinition[];
  language: LanguageCodes;
  currentDefinitionId: number;
}) {
  return allDefinitions.find(
    def =>
      def.id === currentDefinitionId &&
      def.aiDefinitions?.definitions.some(d => d.language === language)
  );
}

export function removeExampleById(examples: FormExample[], id: string) {
  return examples.filter(example => example.exampleId !== id);
}
